/**
 * #13 收禮人領取頁(2026-05-26 channel-less 重做)— LIFF /gift/receive/:code
 *
 * 三階段儀式感:
 *   1. sealed — 進頁不需 LIFF login,call /preview 拿基本資料,顯示「您收到一份來自 OOO 的私享禮遇」
 *   2. opened — 點「展開禮盒」→ 漸現商品 + 祝福卡 + 「我感謝地收下」按鈕
 *   3. received — 接受後顯示「已歸入您的燕窩存摺」
 *
 * URL :code 可吃 short_code (6 字元) 或 gift_id (12 hex,backward compat)。
 *
 * Auth 邏輯:
 *   - 預覽階段不需 LINE login(走 public /api/gifts/preview/:code)
 *   - 「我感謝地收下」按下時若沒有 JWT → 觸發 LIFF init + liff-verify → 拿 JWT → POST receive
 *   - 已是會員 / 已有 JWT → 直接 POST receive
 *   - 自己送自己的 → 跳回 /gift/sent/:code 由 sender 視角看
 */
import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Gift as GiftIcon, Leaf, Mail } from "lucide-react";
import StatusMark from "../components/StatusMark";
import {
  previewGiftByCode,
  receiveGift,
  claimGiftByShipping,
  formatGiftItemQty,
  formatGiftLines,
  type Gift,
  GIFT_TEMPLATES,
} from "../api/gifts";
import { withNext } from "../utils/join";
import { liffVerify } from "../api/members";
import { initLiff } from "../liff";
import { useAuthStore } from "../store/authStore";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import { TAIWAN_CITIES, getDistricts } from "../data/tw-regions";


type Stage =
  | "loading"
  | "sealed"
  | "opening"
  | "opened"
  | "claiming"
  | "choose-claim"   // 2026-05-26 C 方案:非會員選兌換方式(加會員 / 實體寄送)
  | "shipping-form"  // 2026-05-26 C 方案:填實體寄送資料
  | "received"
  | "error";


/**
 * 後端錯誤碼 → 給收禮人看的中文。
 *
 * 2026-09-20:原本領取 / 申請寄送失敗時是 `領取未成功:${r.status}`,把英文代碼
 * 原樣吐給客人 —— 跟 9/16 送禮那次同一個病(錯誤碼是給我們看的,不是給客戶看的)。
 * payment_required 尤其誤導:那代表這份禮物從來沒扣過款,而系統沒有任何路徑能讓它
 * 變成已付款,客人再按一百次都不會成功,該做的是請送禮人聯繫客服。
 */
const CLAIM_ERROR_TEXT: Record<string, string> = {
  payment_required: "這份禮物尚未完成付款,請通知送禮人與專屬顧問聯繫",
  already_received: "這份禮物已經領取過了",
  cancelled: "這份禮物已取消",
  not_found: "找不到這份禮物,連結可能已失效",
  recipient_not_active: "您的會員資格目前無法領取,請與專屬顧問聯繫",
  cannot_receive_own_gift: "這是您送出的禮物,無法自己領取",
};

function claimErrorText(status: string): string {
  return CLAIM_ERROR_TEXT[status] || "領取未成功,請稍後再試或與專屬顧問聯繫";
}


export default function GiftReceive() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);

  const [stage, setStage] = useState<Stage>("loading");
  const [gift, setGift] = useState<Gift | null>(null);
  const [errMsg, setErrMsg] = useState("");
  // 品類明細顯示字串(「即飲 6 瓶 · 留白禮盒 2 組」);舊禮物 fallback「N 瓶」
  const [claimedText, setClaimedText] = useState("");
  // 實體寄送送出中 — 用旗標不用 stage,ShippingForm 才不會被 unmount 洗掉已填的欄位
  const [shippingBusy, setShippingBusy] = useState(false);

  // 初次載入 — public preview(不需 login)
  useEffect(() => {
    (async () => {
      try {
        const r = await previewGiftByCode(id);
        const g = r.gift;
        // sender 自己進來 → 跳回 sent 頁
        // ⚠️ 判斷依據是後端算好的 is_sender,不是 g.sender_uid —— preview 為了防外洩
        // 從來不回 sender_uid,舊版比對那個欄位等於永遠拿到 undefined,這道門從上線到
        // 2026-09-16 沒關起來過一次,送禮人一路走到收件表單填完才被擋(詳 NOTES.md)。
        if (r.is_sender) {
          navigate(`/gift/sent/${id}`, { replace: true });
          return;
        }
        // 已 received 且是自己 → 直接顯示已收下狀態(同上,用 is_recipient 不用 uid 比對)
        if (g.status === "received") {
          if (r.is_recipient) {
            setGift(g);
            setClaimedText(formatGiftLines(g));
            setStage("received");
          } else {
            setErrMsg("此禮物已被領取");
            setStage("error");
          }
          return;
        }
        if (g.status === "cancelled") {
          setErrMsg("此禮物已取消");
          setStage("error");
          return;
        }
        setGift(g);
        setStage("sealed");
      } catch {
        setErrMsg("找不到禮物或連結已失效");
        setStage("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function openBox() {
    setStage("opening");
    // 1.2 秒過渡動畫(CSS),動畫結束切到 opened
    setTimeout(() => setStage("opened"), 1200);
  }

  async function ensureAuth(): Promise<boolean> {
    // 已有 JWT → 不必再 verify
    if (useAuthStore.getState().jwt) return true;
    try {
      const liffProfile = await initLiff();
      if (!liffProfile) {
        // dev mode 或正在跳轉 login,等下次回來
        return false;
      }
      const { jwt, member } = await liffVerify({
        accessToken: liffProfile.accessToken,
        profile: {
          userId: liffProfile.userId,
          displayName: liffProfile.displayName,
          pictureUrl: liffProfile.pictureUrl,
        },
      });
      useAuthStore.getState().setMemberJwt(jwt, member.userId);
      useMemberStore.getState().setMember({
        line_user_id: member.userId,
        name: member.name,
        display_name: member.displayName,
        phone: member.phone, // 同 Splash:結帳 / 送禮的「要不要先補資料」判斷靠這欄
        tier: member.tier,
        status: member.status,
        phone_verified: member.phoneVerified,
      });
      return true;
    } catch (e) {
      console.warn("[ensureAuth] failed", e);
      return false;
    }
  }

  async function claim() {
    setStage("claiming");
    try {
      const authed = await ensureAuth();
      if (!authed) {
        showToast("請先以 LINE 登入,才能領取禮物", "error");
        setStage("opened");
        return;
      }
      // C 方案:會員 → wallet 路徑;非會員 → 切到「兌換選擇」UI
      const m = useMemberStore.getState().member;
      if (!m || m.status !== "active") {
        setStage("choose-claim");
        return;
      }

      const r = await receiveGift(id);
      if (r.status === "ok" && r.gift) {
        showToast("已感謝收下", "success");
        setGift(r.gift);
        setClaimedText(formatGiftLines(r.gift));
        setStage("received");
      } else if (r.status === "already_received_by_other") {
        showToast("此禮物已被他人領取", "error");
        setErrMsg("此禮物已被他人領取");
        setStage("error");
      } else if (r.status === "cannot_receive_own_gift") {
        showToast("無法領取自己送出的禮物", "error");
        navigate(`/gift/sent/${id}`, { replace: true });
      } else if (r.status === "recipient_not_active") {
        // 後端再次拒絕(理論上前面已 branch,defensive)→ 切回 choose-claim
        setStage("choose-claim");
      } else {
        showToast(claimErrorText(r.status), "error");
        console.warn("[GiftReceive] receiveGift 未成功:", r.status);
        setStage("opened");
      }
    } catch {
      showToast("連線異常,請稍後再試", "error");
      setStage("opened");
    }
  }

  /**
   * ⚠️ 這裡刻意**不切 stage** —— 停在 shipping-form,只用 shippingBusy 表示送出中。
   * 舊版一送出就 setStage("claiming"),ShippingForm 整個 unmount,失敗切回來時
   * 重新掛載 → 姓名手機地址全部被清空,人得整份重打(2026-09-16 謝睿朋截圖裡
   * 欄位是空的就是這樣來的)。
   */
  async function submitShipping(payload: {
    name: string;
    phone: string;
    city: string;
    district: string;
    detail: string;
  }) {
    setShippingBusy(true);
    try {
      const r = await claimGiftByShipping(id, {
        recipient_name: payload.name,
        recipient_phone: payload.phone,
        shipping_address: {
          city: payload.city,
          district: payload.district,
          detail: payload.detail,
        },
      });
      if (r.status === "ok" && r.gift) {
        showToast("已申請實體寄送", "success");
        setGift(r.gift);
        setClaimedText(formatGiftLines(r.gift));
        setStage("received");
      } else if (r.status === "cannot_receive_own_gift") {
        // 理論上載入時的 is_sender 守門就擋掉了,這裡是最後一道(例如另一個分頁登入了別的帳號)
        showToast("這是您送出的禮物,無法自己領取", "error");
        navigate(`/gift/sent/${id}`, { replace: true });
      } else if (r.status === "already_received_by_other") {
        showToast("此禮物已被他人領取", "error");
        setErrMsg("此禮物已被他人領取");
        setStage("error");
      } else if (r.status === "invalid_phone") {
        showToast("手機格式有誤,請輸入 09 開頭十位數", "error");
      } else if (r.status === "missing_address" || r.status === "missing_recipient") {
        showToast("請完整填寫姓名、手機與地址", "error");
      } else if (r.status === "not_found") {
        showToast("找不到這份禮物,連結可能已失效", "error");
      } else {
        showToast(claimErrorText(r.status), "error");
        console.warn("[GiftReceive] claimGiftByShipping 未成功:", r.status);
      }
    } catch {
      showToast("連線異常,請稍後再試", "error");
    } finally {
      setShippingBusy(false);
    }
  }

  // ─────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────

  if (stage === "loading") {
    return (
      <div className="min-h-screen bg-primary-dark flex items-center justify-center text-white/60 text-sm">
        正在打開您的禮遇…
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4 px-6 text-center">
        <StatusMark icon={Leaf} />
        <div className="text-base text-ink">{errMsg}</div>
        <button onClick={() => navigate("/home")} className="btn-primary mt-2">回會員首頁</button>
      </div>
    );
  }

  if (stage === "received" && gift) {
    return <ReceivedView gift={gift} linesText={claimedText || formatGiftLines(gift)} onHome={() => navigate("/wallet")} />;
  }

  if (!gift) return null;

  const tpl = GIFT_TEMPLATES.find((t) => t.key === gift.blessing_card.template);
  const message = gift.blessing_card.message || tpl?.defaultMessage || "";
  const senderName = gift.sender_name || "霽雲會員";

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-dark to-primary text-white pb-12 flex flex-col">
      {/* Stage: sealed */}
      {stage === "sealed" && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center fade-in">
          <div className="text-xs tracking-widest opacity-70 mb-3">霽雲・恆時珍藏</div>
          <StatusMark icon={Mail} size="lg" tone="onDark" className="mb-6 animate-soft-float" />
          <div className="text-lg font-bold opacity-90 mb-1">您收到一份私享禮遇</div>
          <div className="text-base opacity-80 mb-10">
            來自 <span className="font-bold">{senderName}</span>
          </div>
          <button
            onClick={openBox}
            className="bg-accent text-white font-bold py-3 px-12 rounded-full shadow-lg hover:opacity-90"
          >
            展開禮盒
          </button>
          <div className="text-xs opacity-50 mt-6 px-4 leading-relaxed">
            您尚未需要登入即可預覽。
            <br />
            願意接受這份禮物時,將請您以 LINE 登入。
          </div>
        </div>
      )}

      {/* Stage: opening — 短暫過渡動畫 */}
      {stage === "opening" && (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <StatusMark icon={GiftIcon} size="lg" tone="onDark" className="animate-gift-open" />
          <div className="text-sm opacity-80 mt-6 tracking-wider">展開中…</div>
        </div>
      )}

      {/* Stage: opened / claiming — 展開後的內容 */}
      {(stage === "opened" || stage === "claiming") && (
        <div className="flex-1 px-5 pt-8 pb-32 fade-in space-y-5">
          <div className="text-center">
            <div className="text-xs tracking-widest opacity-70">霽雲・恆時珍藏</div>
            <div className="text-2xl font-bold mt-2">來自 {senderName} 的私享</div>
          </div>

          {/* 祝福卡 */}
          <div className="rounded-2xl bg-cream/95 text-ink p-6 text-center shadow-xl space-y-4 mt-6">
            <div className="text-xs text-muted tracking-wider">{tpl?.label} 祝福卡</div>
            <div className="text-base leading-relaxed whitespace-pre-wrap break-words py-3">
              {message}
            </div>
            <div className="text-xxs text-muted">— {senderName} 以霽雲之名</div>
          </div>

          {/* 禮物內容 — 不顯示金額,只顯示瓶數,維持儀式感 */}
          <div className="rounded-2xl bg-white/10 backdrop-blur p-4 space-y-2">
            <div className="text-sm opacity-80 font-medium">禮物內容</div>
            <div className="space-y-1.5 text-sm">
              {gift.items.map((it) => (
                <div key={it.product_id} className="flex justify-between">
                  <span className="flex-1">{it.name} <span className="opacity-70">× {it.qty}</span></span>
                  <span className="opacity-90">{formatGiftItemQty(it)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/20 mt-2 pt-2 flex justify-between text-sm">
              <span className="opacity-70">合計</span>
              <span className="font-bold">{formatGiftLines(gift)}</span>
            </div>
          </div>

          <div className="text-xs opacity-60 text-center leading-relaxed mt-4 px-4">
            領取後,瓶數將歸入您的「燕窩存摺」。
            <br />
            可於會員頁面隨時查看與兌換。
          </div>
        </div>
      )}

      {/* CTA 底部固定 — claiming 顯示中,opened 顯示按鈕 */}
      {(stage === "opened" || stage === "claiming") && (
        <div className="fixed bottom-0 left-0 right-0 px-5 py-4 bg-gradient-to-t from-primary-dark via-primary-dark/95 to-transparent">
          <button
            onClick={claim}
            disabled={stage === "claiming"}
            className="w-full bg-accent text-white font-bold py-3.5 rounded-full shadow-2xl disabled:opacity-60"
          >
            {stage === "claiming" ? "領取中…" : "我感謝地收下"}
          </button>
        </div>
      )}

      {/* Stage: choose-claim — 非會員兌換選擇(C 方案,2026-05-26)*/}
      {stage === "choose-claim" && (
        <div className="flex-1 flex flex-col px-5 pt-8 pb-8 fade-in">
          <div className="text-center mb-6">
            <div className="text-xs tracking-widest opacity-70">兌換方式</div>
            <div className="text-xl font-bold mt-2">為您備好兩條路</div>
            <div className="text-sm opacity-70 mt-2">請選擇收下這份心意的方式</div>
          </div>

          {/* 主 CTA — 加入會員(大)。一鍵加入完帶 next 回這頁,接著按「收下」就進存摺 */}
          <button
            onClick={() => navigate(withNext("/join", `/gift/receive/${id}`))}
            className="rounded-2xl bg-accent text-white p-5 text-left shadow-xl"
          >
            <div className="text-base font-bold">成為霽雲會員 · 帶走 {formatGiftLines(gift)}</div>
            <div className="text-xs opacity-90 mt-2 leading-relaxed">
              以霽雲之名為您開啟燕窩存摺,
              這份心意將成為長期相伴的滋養,
              亦可隨時兌換為實體商品。
            </div>
            <div className="text-xs font-bold mt-3">→ 完成入會(約 1 分鐘)</div>
          </button>

          {/* 次 CTA — 實體寄送(小) */}
          <button
            onClick={() => setStage("shipping-form")}
            className="rounded-2xl border border-white/30 text-white p-4 text-left mt-3"
          >
            <div className="text-sm font-bold">我目前不便加入</div>
            <div className="text-xs opacity-75 mt-1.5 leading-relaxed">
              填寫寄送資訊,專屬顧問將為您安排實體禮盒寄送
            </div>
          </button>

          <div className="text-xxs opacity-50 text-center mt-6 px-2 leading-relaxed">
            無論選擇哪條路,這份心意都不會被退回。
            <br />
            霽雲會員制無年費、可長期累積。
          </div>
        </div>
      )}

      {/* Stage: shipping-form — 非會員填寄送資訊 */}
      {stage === "shipping-form" && (
        <ShippingForm
          linesText={formatGiftLines(gift)}
          submitting={shippingBusy}
          onSubmit={submitShipping}
          onBack={() => setStage("choose-claim")}
        />
      )}
    </div>
  );
}


/**
 * submitting 由父層傳進來(不是自己管)—— 父層送出失敗時 stage 停在 shipping-form,
 * 這個元件不會被 unmount,已填的欄位就留著,人不用重打一次。
 */
function ShippingForm({
  linesText,
  submitting,
  onSubmit,
  onBack,
}: {
  linesText: string;
  submitting: boolean;
  onSubmit: (payload: { name: string; phone: string; city: string; district: string; detail: string }) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [detail, setDetail] = useState("");
  const showToast = useUIStore((s) => s.showToast);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const n = name.trim();
    const p = phone.trim().replace(/-/g, "");
    if (!n) { showToast("請填寫姓名", "error"); return; }
    if (!/^09\d{8}$/.test(p)) { showToast("手機格式有誤,請輸入 09 開頭十位數", "error"); return; }
    if (!city || !detail.trim()) { showToast("請填寫縣市與詳細地址", "error"); return; }
    onSubmit({ name: n, phone: p, city, district, detail: detail.trim() });
  }

  return (
    <div className="flex-1 px-5 pt-6 pb-32 fade-in">
      <div className="text-center mb-6">
        <div className="text-xs tracking-widest opacity-70">實體寄送</div>
        <div className="text-xl font-bold mt-2">收件資訊</div>
        <div className="text-sm opacity-70 mt-2">
          專屬顧問將為您安排實體寄送({linesText})
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs opacity-80 block mb-1">姓名 *</label>
          <input
            className="w-full bg-white/95 text-ink rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-accent"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="收件人姓名"
            required
          />
        </div>

        <div>
          <label className="text-xs opacity-80 block mb-1">手機 *</label>
          <input
            className="w-full bg-white/95 text-ink rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-accent"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0912345678"
            required
          />
        </div>

        <div>
          <label className="text-xs opacity-80 block mb-1">縣市 *</label>
          <select
            className="w-full bg-white/95 text-ink rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-accent"
            value={city}
            onChange={(e) => { setCity(e.target.value); setDistrict(""); }}
            required
          >
            <option value="">請選擇縣市</option>
            {TAIWAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs opacity-80 block mb-1">區域</label>
          <select
            className="w-full bg-white/95 text-ink rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            disabled={!city}
          >
            <option value="">{city ? "請選擇區域" : "請先選擇縣市"}</option>
            {getDistricts(city).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs opacity-80 block mb-1">詳細地址 *</label>
          <textarea
            className="w-full bg-white/95 text-ink rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-accent resize-none"
            rows={2}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="路名、巷弄、號碼、樓層"
            required
          />
        </div>

        <div className="text-xxs opacity-60 leading-relaxed pt-1">
          專屬顧問將於 1-2 個工作日與您聯繫,確認寄送細節。
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="flex-1 border border-white/40 text-white rounded-full py-3 text-sm disabled:opacity-50"
          >
            上一步
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-accent text-white font-bold rounded-full py-3 disabled:opacity-60"
          >
            {submitting ? "送出中…" : "確認寄送"}
          </button>
        </div>
      </form>
    </div>
  );
}


function ReceivedView({ gift, linesText, onHome }: { gift: Gift; linesText: string; onHome: () => void }) {
  const tpl = GIFT_TEMPLATES.find((t) => t.key === gift.blessing_card.template);
  const message = gift.blessing_card.message || tpl?.defaultMessage || "";
  const senderName = gift.sender_name || "霽雲會員";

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-dark to-primary text-white pb-12">
      <header className="px-5 py-6 text-center">
        <div className="text-xs tracking-widest opacity-70">霽雲・恆時珍藏</div>
        <h1 className="text-2xl font-bold mt-2">禮物已收下</h1>
      </header>

      <div className="px-5 mt-2 space-y-4">
        <div className="text-center py-6">
          <StatusMark icon={Check} size="lg" tone="onDark" className="mb-3" />
          <div className="text-lg font-bold">
            已收下 {linesText}
          </div>
          <div className="text-sm opacity-80 mt-2">已歸入您的燕窩存摺</div>
        </div>

        <div className="rounded-2xl bg-cream/95 text-ink p-6 text-center shadow-xl space-y-4">
          <div className="text-xs text-muted tracking-wider">{tpl?.label} 祝福卡</div>
          <div className="text-base leading-relaxed whitespace-pre-wrap break-words py-3">
            {message}
          </div>
          <div className="text-xxs text-muted">— {senderName} 以霽雲之名</div>
        </div>

        <button onClick={onHome} className="w-full bg-accent text-white font-bold py-3.5 rounded-full shadow-xl mt-6">
          查看我的燕窩存摺
        </button>
      </div>
    </div>
  );
}
